import {
    FeatureFlagName,
    FeatureFlagNameRuntype,
    ListFeatureFlagsResponse,
    ProductCostingAccess,
    StepEnum,
    UserType,
} from '@luminovo/api-contracts';
import { Permission } from '@luminovo/auth';
import { isProductionEnvironment } from '@luminovo/commons';
import { useSuspenseHttpQuery } from '@luminovo/data-stack';

import { useHasModulePermissions, useIsAdvancedRoleManagementEnabled } from '@/shared/permissions/useModulePermissions';

import { ADMIN_PERMISSIONS, useCurrentUserDetailsContext } from '../data/contexts/CurrentUserDetailsContext';

export const MANUFACTURING_LITE_PERMISSIONS: Permission[] = [
    'view:resource',
    'view:driver',
    'view:expense',
    'view:activity',
    'view:activity_configuration',
    'create:activity_configuration',
    'edit:activity_configuration',
    'view:expense_configuration',
    'create:expense_configuration',
    'edit:expense_configuration',
    'view:manufacturing_scenario_template',
    'view:manually_overwritten_driver_count',
    'create:manually_overwritten_driver_count',
    'edit:manually_overwritten_driver_count',
    'create:manufacturing_assembly_details',
    'view:manufacturing_assembly_details',
    'edit:manufacturing_assembly_details',
    'view:manufacturing_scenario',
    'create:manufacturing_scenario',
    'edit:manufacturing_scenario',
    'view:frozen_scenario_cost',
];

export const MANUFACTURING_PAID_PERMISSIONS: Permission[] = [
    'edit:resource',
    'create:resource',
    'edit:driver',
    'create:driver',
    'edit:expense',
    'create:expense',
    'edit:activity',
    'create:activity',
    'create:manufacturing_scenario_template',
    'edit:manufacturing_scenario_template',
    'delete:manufacturing_scenario_template',
    'edit:calculation_template',
    'delete:calculation_template',
    'create:calculation_template',
    'view:scenario_combinations_for_calculation',
    'create:scenario_combinations_for_calculation',
    'edit:scenario_combinations_for_calculation',
    'view:calculation_assembly_details',
    'create:calculation_assembly_details',
    'view:calculation',
    'edit:calculation_assembly_details',
    'view:calculation_assembly_cost',
    'create:calculation_assembly_cost',
    'edit:calculation_assembly_cost',
    'create:frozen_scenario_cost',
    'edit:frozen_scenario_cost',
];

// TODO: once all tenants use advanced roles, remove the useProtected check
export const useProductCostingAccess = (): ProductCostingAccess => {
    const { data: productCostingAccess } = useSuspenseHttpQuery(
        'GET /my/feature-flags',
        {},
        { select: (data) => data.product_costing_access, staleTime: Infinity },
    );

    return productCostingAccess;
};

/** A non-empty list, so routes can declare it as `staticData.access.permissions`. */
export const CALCULATION_PERMISSIONS: [Permission, ...Permission[]] = ['view:calculation'];

/**
 * Customer users may view PnP files only when `upload_pnp_files` lets them upload them; viewing and downloading needs
 * `view`. Importing and deleting are gated separately, by `isRfqEditable` (which requires `edit`).
 * The session doesn't load the setting: the first page to ask waits for it.
 */
export const useCanViewPnp = () => {
    const { userType } = useCurrentUserDetailsContext();
    const { hasModulePermissions } = useHasModulePermissions();
    const { data: uploadPnpFiles } = useSuspenseHttpQuery(
        'GET /customer-portal-settings',
        {},
        { select: (data) => data.upload_pnp_files, staleTime: Infinity },
    );
    const canCustomerUploadPnpFiles = uploadPnpFiles === StepEnum.required || uploadPnpFiles === StepEnum.optional;

    return {
        canViewPnp:
            (userType !== UserType.Customer || canCustomerUploadPnpFiles) &&
            hasModulePermissions({
                type: 'any',
                permissions: [
                    { module: 'design', permission: 'view' },
                    { module: 'customer_portal', permission: 'view' },
                ],
            }),
    };
};

export const MANUFACTURING_ADMIN_PERMISSIONS: Permission[] = [...ADMIN_PERMISSIONS, ...MANUFACTURING_LITE_PERMISSIONS];

export const QUOTATIONS_V2_ADMIN_PERMISSIONS: Permission[] = [...ADMIN_PERMISSIONS, ...CALCULATION_PERMISSIONS];

const assemblyPortalEnvironments = [
    'luminovo.luminovo.com',
    'demo.luminovo.com',
    'luminovo.luminovo.ai',
    'luminovo.staging.luminovo.ai',
    'luminovo.staging.luminovo.com',
    'staging.luminovo.ai',
    'staging.luminovo.com',

    'bmk-customer.luminovo.com',

    'gpv-fastproto.luminovo.com',

    'zollner-ccp.luminovo.com',
    'zollner-demo.luminovo.com',
    'zollner-demo-ccp.luminovo.com',
];

export const isAssemblyPortalEnabled = (): boolean => {
    return (
        !isProductionEnvironment() || assemblyPortalEnvironments.some((host) => window.location.host.startsWith(host))
    );
};

export const isOrderPortalEnabled = (): boolean => {
    return isAssemblyPortalEnabled();
};

export const isPurchasingEnabled = (): boolean => {
    return (
        !isProductionEnvironment() ||
        [
            'luminovo.luminovo.com',
            'luminovo.luminovo.ai',
            'luminovo.staging.luminovo.com',
            'luminovo.staging.luminovo.ai',
            'staging.luminovo.ai',
            'staging.luminovo.com',

            'zollner-americas.luminovo.com',
            'zollner-ccp.luminovo.com',
            'zollner-china.luminovo.com',
            'zollner-demo.luminovo.com',
            'zollner-quoting.luminovo.com',
            'zollner-usa.luminovo.com',
            'zollner.luminovo.com',

            // High usage tenants (10+ purchase orders)
            'connectgroup.luminovo.com',
            'mycicor.luminovo.com',
            'pcbwhiz-eu.luminovo.com',
            'pcbwhiz-apac.luminovo.com',
            'glueck-elektronik.luminovo.com',

            // Keep existing tenants for backward compatibility
            'connectgroup-dev.luminovo.com',
            'cicor.luminovo.com',
            'cicor-group.luminovo.com',
            'cicor-smt.luminovo.com',
            'pcbwhiz.luminovo.com',

            'fermionx.luminovo.com',
        ].some((host) => window.location.host.startsWith(host))
    );
};

const customerRfQDisabledEnvironments = ['bmk-customer.luminovo.com', 'zollner-ccp.luminovo.com'];

export const isCustomerRfQDisabled = (): boolean => {
    return customerRfQDisabledEnvironments.some((host) => window.location.host.startsWith(host));
};

export const useShowWinRate = (): { showWinRate: boolean } => {
    const { userType } = useCurrentUserDetailsContext();
    return { showWinRate: userType !== UserType.Customer };
};

export const useIsRoleSettingsEnabled = (): { isRoleSettingsEnabled: boolean } => ({
    isRoleSettingsEnabled: useIsAdvancedRoleManagementEnabled(),
});

export const useIsCsvTextPasteImportEnabled = (): { isCsvTextPasteImportEnabled: boolean } => {
    return { isCsvTextPasteImportEnabled: !isProductionEnvironment() };
};

export const useIsDemandGroupsEnabled = (): { isDemandGroupsEnabled: boolean } => {
    return { isDemandGroupsEnabled: useFeatureFlag().is_demand_groups_enabled };
};

/**
 * Hook to check if web search is enabled for Lumi on the current tenant.
 * Gated by the per-tenant `lumi_web_search` feature flag (toggled without a deploy
 * via the Tenants Retool app), and always on outside production for easy
 * dev/preview testing. Web search is disabled by default for data privacy
 * reasons (it routes to US/OpenAI). This is UI-only; the gateway independently
 * enforces the same flag server-side (see isWebSearchEnabledForUser).
 */
export const useIsWebSearchEnabled = (): boolean => useFeatureFlag().lumi_web_search || !isProductionEnvironment();

// Line cards aren't maintained for GPV tenants, so point users to the Approved Vendor List.
export const isSupplierLineCardDisabled = (): boolean => {
    return window.location.host.startsWith('gpv-');
};

/** Opt-in to `combinePriceBreaks`: each break then applies from its MOQ up, which not all suppliers mean. */
export const useIsQuoteImportCombinePriceBreaksEnabled = (): boolean =>
    useFeatureFlag().quote_import_combine_price_breaks || !isProductionEnvironment();

const FEATURE_FLAGS_STALE_TIME_MS = 5 * 60 * 1000;

export type FeatureFlags = Record<FeatureFlagName, boolean>;

/** Every feature flag, on or off, from `GET /my/feature-flags`. */
export const selectFeatureFlags = (res: ListFeatureFlagsResponse): FeatureFlags => {
    const enabled = new Set(res.data.filter((f) => f.enabled).map((f) => f.feature_flag));
    return Object.fromEntries(FeatureFlagNameRuntype.options.map((name) => [name, enabled.has(name)])) as FeatureFlags;
};

/**
 * The feature flags for the logged-in user, with their user-specific overrides applied.
 * Suspends until the backend answers.
 */
export const useFeatureFlag = (): FeatureFlags => {
    const { data } = useSuspenseHttpQuery(
        'GET /my/feature-flags',
        {},
        { staleTime: FEATURE_FLAGS_STALE_TIME_MS, select: selectFeatureFlags },
    );
    return data;
};

export const useHasProjectProgress = (): boolean => {
    const { project_progress } = useFeatureFlag();
    const { userType } = useCurrentUserDetailsContext();
    return project_progress && userType === UserType.Internal;
};

/**
 * Redesign of the Manufacturing Scenario details page (MAN-1902): scrollable page,
 * full-width TanStack activities/expenses table with expandable rows, batched driver
 * editing.
 */
export const useIsManufacturingScenarioRedesignEnabled = (): boolean =>
    useFeatureFlag().manufacturing_scenario_redesign || !isProductionEnvironment();
